import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type { ISiteOption } from "@/customTypings/customer/Customer";
import type {
    CustomerLocationAssociatePayload,
    CustomerLocationUpdatePayload,
    ICustomerLocationAssociation,
    ICustomerSelectItem,
    ILocationDetail,
    ILocationMapConfig,
    LocationCreatePayload,
    LocationUpdatePayload,
} from "@/customTypings/location/Location";
import { authHeader, handleResponse } from "../../utils";

export const locationsService = {
    getMapConfig,
    getById,
    create,
    update,
    disable,
    enable,
    softDelete,
    restore,
    listCustomerAssociations,
    listLinkableSitesForCustomerAssociation,
    listAvailableCustomersForAssociation,
    associateCustomer,
    updateCustomerAssociation,
    setMainCustomerAssociation,
    unlinkCustomerAssociation,
};

function getMapConfig() {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/locations/mapconfig`, requestOptions).then(handleResponse) as Promise<ILocationMapConfig>;
}

export type LocationGetByIdOptions = {
    /** When false, audit display fields are omitted (faster for entity-only reads). Default true. */
    includeAudit?: boolean;
};

/** Loads a location detail. Pass `{ includeAudit: false }` when audit bar fields are not needed. */
function getById(id: string, options?: LocationGetByIdOptions) {
    const params = new URLSearchParams();
    if (options?.includeAudit === false) {
        params.set("includeAudit", "false");
    }
    const query = params.toString();
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    const url = `/api/locations/${encodeURIComponent(id)}${query ? `?${query}` : ""}`;
    return fetch(url, requestOptions).then(handleResponse) as Promise<ILocationDetail>;
}

function create(payload: LocationCreatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/locations/create`, requestOptions).then(handleResponse) as Promise<ILocationDetail>;
}

function update(payload: LocationUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/locations/update`, requestOptions).then(handleResponse) as Promise<
        UpdateMutationResult<ILocationDetail>
    >;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/locations/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(handleResponse);
}

function listCustomerAssociations(locationId: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations`,
        requestOptions
    ).then(handleResponse) as Promise<ICustomerLocationAssociation[]>;
}

function listLinkableSitesForCustomerAssociation(locationId: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations/linkable-sites`,
        requestOptions
    ).then(handleResponse) as Promise<ISiteOption[]>;
}

function listAvailableCustomersForAssociation(locationId: string, siteId: string, q?: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    const params = new URLSearchParams({ siteId });
    if (q?.trim()) {
        params.set("q", q.trim());
    }
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations/available-customers?${params.toString()}`,
        requestOptions
    ).then(handleResponse) as Promise<ICustomerSelectItem[]>;
}

function associateCustomer(locationId: string, payload: CustomerLocationAssociatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations/associate`,
        requestOptions
    ).then(handleResponse) as Promise<ICustomerLocationAssociation>;
}

function updateCustomerAssociation(locationId: string, payload: CustomerLocationUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations/update`,
        requestOptions
    ).then(handleResponse);
}

function setMainCustomerAssociation(locationId: string, associationId: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations/set-main?id=${encodeURIComponent(associationId)}`,
        requestOptions
    ).then(handleResponse);
}

function unlinkCustomerAssociation(locationId: string, associationId: string) {
    return postAssociationQuery(locationId, "unlink", associationId);
}

function postAssociationQuery(locationId: string, action: string, associationId: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/locations/${encodeURIComponent(locationId)}/customer-associations/${action}?id=${encodeURIComponent(associationId)}`,
        requestOptions
    ).then(handleResponse);
}
